import React from "react";
import { KpiIcon } from "../../../DataManagment/Quantative/component/KpiIcons";
import { formatNum, scopeKey } from "./helpers";
import { CATEGORY_COLORS, CATEGORY_COLOR_FALLBACK } from "./constants";

/**
 * Display a numeric cell value, trimmed of trailing zeros.
 *
 * @param {*} val  the cell's raw value
 * @returns {string} the formatted number, or an em-dash when there is no value
 */
const displayVal = (val) => {
  const f = formatNum(val);
  return f === null || f === undefined || f === "" ? "—" : f;
};

/**
 * Colour for a category dot.
 *
 * @param {number} questionId  the row's question (KPI) id
 * @returns {string} the configured hex colour, or the grey fallback when the
 *   question has no entry in CATEGORY_COLORS
 */
const categoryColor = (questionId) =>
  CATEGORY_COLORS[questionId] || CATEGORY_COLOR_FALLBACK;

/**
 * Escape a value for a CSV cell (RFC 4180): a field containing a comma, quote,
 * or newline is wrapped in double quotes with inner quotes doubled, so names
 * with those characters can't shift the columns after them.
 *
 * @param {*} cell  the value to write; strings are trimmed, null/undefined become ""
 * @returns {string} the cell, quoted only when it needs to be
 */
const escapeCsv = (cell) => {
  const s =
    typeof cell === "string" ? cell.trim() : cell == null ? "" : String(cell);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Shared value-pill style, so every Min / Max / Actual / Difference cell shares
 * one set of padding, radius and weight and differs only in colour.
 *
 * @param {string} bg  background colour
 * @param {string} border  border colour; "transparent" drops the border entirely
 * @param {string} color  text colour
 * @returns {Object} the inline style object for the pill
 */
const valueBox = (bg, border, color) => ({
  padding: "8px 12px",
  backgroundColor: bg,
  border: border === "transparent" ? "none" : `2px solid ${border}`,
  borderRadius: "8px",
  fontSize: "13px",
  fontWeight: "700",
  color,
});

/** Inline style shared by every centred value cell in a row. */
const cellBase = {
  padding: "8px",
  textAlign: "center",
  borderBottom: "1px solid #e5e7eb",
};

/**
 * A single analytics row — category name, its Min/Max targets, the actual
 * reading, and the resulting difference and remark. Memoised on primitive props
 * so re-rendering one scope's table doesn't re-render every row.
 *
 * @param {Object} props
 * @param {number} props.index  zero-based position within the scope, shown as the
 *   row number and deciding the stripe
 * @param {number} props.questionId  the question (KPI) id, which picks the dot colour
 * @param {string} props.name  category label
 * @param {*} props.min  minimum trigger target
 * @param {*} props.max  maximum trigger target
 * @param {*} props.actual  reported value for the selected month
 * @param {number|string} props.difference  actual − max, or "" when incomparable
 * @param {string} props.remark  "Within Limit", "Exceeding Limit", or ""
 * @param {string} props.stripeColor  background for even rows
 */
const AnalyticsRow = React.memo(function AnalyticsRow({
  index,
  questionId,
  name,
  min,
  max,
  actual,
  difference,
  remark,
  stripeColor,
}) {
  const diffText = difference === "" ? "" : formatNum(difference);
  const within = remark === "Within Limit";
  const exceeding = remark === "Exceeding Limit";

  return (
    <tr style={{ backgroundColor: index % 2 === 0 ? stripeColor : "#fff" }}>
      <td style={{ ...cellBase, fontWeight: "600", color: "#6b7280" }}>
        {index + 1}
      </td>
      <td
        style={{
          padding: "12px",
          fontWeight: "600",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "16px",
              height: "16px",
              backgroundColor: categoryColor(questionId),
              borderRadius: "50%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              border: "2px solid #fff",
            }}
          />
          <span style={{ color: "#374151" }}>{name}</span>
        </div>
      </td>
      <td style={cellBase}>
        <div style={valueBox("#f8fafc", "#e2e8f0", "#475569")}>
          {displayVal(min)}
        </div>
      </td>
      <td style={cellBase}>
        <div style={valueBox("#f8fafc", "#e2e8f0", "#475569")}>
          {displayVal(max)}
        </div>
      </td>
      <td style={cellBase}>
        <div style={valueBox("#f0f9ff", "#0ea5e9", "#0369a1")}>
          {displayVal(actual)}
        </div>
      </td>
      <td style={cellBase}>
        <div
          style={valueBox(
            diffText === "" ? "transparent" : within ? "#f0fdf4" : "#fef2f2",
            diffText === "" ? "transparent" : within ? "#86efac" : "#fca5a5",
            diffText === "" ? "transparent" : within ? "#16a34a" : "#dc2626"
          )}
        >
          {diffText}
        </div>
      </td>
      <td style={cellBase}>
        {within || exceeding ? (
          <div
            style={{
              ...valueBox(
                within ? "#ecfdf5" : "#fef2f2",
                within ? "#22c55e" : "#ef4444",
                within ? "#15803d" : "#dc2626"
              ),
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <KpiIcon
              name={within ? "check" : "alert"}
              size={13}
              color={within ? "#15803d" : "#dc2626"}
            />
            {remark}
          </div>
        ) : (
          ""
        )}
      </td>
    </tr>
  );
});

/**
 * A table header cell on the accent bar, with an optional leading icon.
 *
 * @param {Object} props
 * @param {string} [props.icon]  KpiIcon name to draw before the label
 * @param {React.ReactNode} props.children  the header label
 * @param {boolean} [props.left]  left-align and widen the column, for the category
 *   name column rather than a value column
 */
const TH = ({ icon, children, left }) => (
  <th
    style={{
      padding: "16px 12px",
      fontWeight: "600",
      textAlign: left ? "left" : "center",
      minWidth: left ? "220px" : "120px",
      borderRight: "1px solid rgba(255,255,255,0.2)",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: left ? "flex-start" : "center",
        gap: "8px",
      }}
    >
      {icon && <KpiIcon name={icon} size={15} color="#fff" />}
      <span>{children}</span>
    </div>
  </th>
);

/**
 * Presentational analytics table shared by the Bio-Medical and Water tabs. All
 * values arrive pre-computed from useAnalyticsData (`byLocation` keyed by
 * `scopeKey` — source plus sub-location — → rows). Visual identity (title, icon,
 * accent gradient, row label, export filename) is driven entirely by props; this
 * component holds no business logic.
 *
 * @param {Object} props
 * @param {string} props.title  heading above the table
 * @param {string} props.subtitle  one-line description under the heading
 * @param {string} props.headerIcon  KpiIcon name for the heading
 * @param {string} props.rowLabel  name of the category column ("Waste Type", "Water Category")
 * @param {string} props.rowLabelIcon  KpiIcon name for that column's header
 * @param {string} props.accentGradient  CSS gradient for the header bar and spinner panel
 * @param {string} [props.stripeColor="#fafbff"]  background for even rows
 * @param {string} props.loadingLabel  text shown while the tab loads
 * @param {string} props.emptyLabel  text shown when the FY has none of the tab's questions
 * @param {string} props.exportPrefix  first segment of the exported CSV's filename
 * @param {Array} props.categories  the tab's rows in display order; an empty list
 *   renders the empty state
 * @param {Object} props.byLocation  `scopeKey` → `{ rows }`, the values to render
 * @param {boolean} props.loading  show the blocking spinner
 * @param {string|null} props.error  message to show in place of the table
 * @param {Function} props.refresh  retry handler for the error state
 * @param {string} props.selectedMonth  `YYYY-MM`, used in the export filename
 * @param {Array} props.locations  the scopes to render, in order, each
 *   `{ id, subLocationId, name }` — one section per entry
 * @param {Object} props.sourceLabels  scope key → display name, for the export filename
 * @param {string[]} props.sourceIds  every selectable scope key; a single one means
 *   there is nothing to disambiguate, so section headers are dropped
 * @param {string[]} props.selectedSourceIds  the picked scope keys, for the export filename
 * @param {Array} props.financialYear  the FY list, for the export filename
 * @param {number} props.financialYearId  the selected FY, for the export filename
 */
const AnalyticsTable = ({
  title,
  subtitle,
  headerIcon,
  rowLabel,
  rowLabelIcon,
  accentGradient,
  stripeColor = "#fafbff",
  loadingLabel,
  emptyLabel,
  exportPrefix,
  categories,
  byLocation,
  loading,
  error,
  refresh,
  selectedMonth,
  locations,
  sourceLabels,
  sourceIds,
  selectedSourceIds,
  financialYear,
  financialYearId,
}) => {
  /**
   * The rows to render under one scope's section heading.
   *
   * @param {{id: number, subLocationId: (number|null)}} location  the scope
   * @returns {Array} that scope's rows, or `[]` when it reported nothing
   */
  const rowsFor = (location) => {
    const scope = byLocation[scopeKey(location.id, location.subLocationId)];
    return (scope && scope.rows) || [];
  };

  /**
   * Download the table as CSV — one line per scope × category, prefixed with the
   * scope name so KMC-T1 and KMC-T2 stay distinguishable in the file. The
   * filename records the FY, the location selection and the month it was taken
   * from, so a saved export can't be mistaken for a different period's.
   */
  const exportToExcel = () => {
    const exportData = [
      [
        "Location",
        rowLabel,
        "Min Trigger Value",
        "Max Trigger Value",
        "Actual Value",
        "Difference",
        "Remark",
      ],
    ];

    locations.forEach((location) => {
      rowsFor(location).forEach((row) => {
        exportData.push([
          location.name,
          row.name,
          displayVal(row.min),
          displayVal(row.max),
          displayVal(row.actual),
          row.difference === "" ? "" : formatNum(row.difference),
          row.remark,
        ]);
      });
    });

    const csvContent = exportData
      .map((r) => r.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download === undefined) return;

    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    const currentDate = new Date().toISOString().split("T")[0];
    const selectedYear = (financialYear || []).find(
      (year) => year.id === financialYearId
    );
    const yearText = selectedYear
      ? selectedYear.financial_year_value
      : "Unknown";
    const locationText =
      selectedSourceIds.length === 0 ||
      selectedSourceIds.length === sourceIds.length
        ? "All_Locations"
        : selectedSourceIds.length === 1
        ? sourceLabels[selectedSourceIds[0]] || "Unknown_Location"
        : "Multiple_Locations";
    const monthText = selectedMonth
      ? selectedMonth.replace("-", "_")
      : "All_Months";

    link.setAttribute(
      "download",
      `${exportPrefix}_${yearText}_${locationText}_${monthText}_${currentDate}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * The table body, or whichever single state stands in for it: the loading
   * spinner, the error panel with its retry, or the empty-state message.
   *
   * @returns {React.ReactNode} the panel to render below the heading
   */
  const renderBody = () => {
    if (loading) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "60px",
            background: accentGradient,
            borderRadius: "12px",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid rgba(255,255,255,0.3)",
              borderTop: "4px solid #fff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <div style={{ fontSize: "18px", fontWeight: "500", color: "#fff" }}>
            {loadingLabel}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "48px",
            background: "#fff",
            borderRadius: "12px",
            color: "#b91c1c",
          }}
        >
          <div style={{ marginBottom: "16px", fontWeight: 600 }}>{error}</div>
          <button
            onClick={refresh}
            style={{
              padding: "10px 20px",
              background: "#667eea",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!categories.length) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "48px",
            background: "#fff",
            borderRadius: "12px",
            color: "#6b7280",
          }}
        >
          {emptyLabel}
        </div>
      );
    }

    const singleLocation = sourceIds && sourceIds.length === 1;

    return (
      <div
        style={{
          overflowX: "auto",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          background: "#fff",
        }}
      >
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}
        >
          <thead>
            <tr style={{ background: accentGradient, color: "#fff" }}>
              <th
                style={{
                  padding: "16px 12px",
                  fontWeight: "600",
                  textAlign: "center",
                  minWidth: "50px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                #
              </th>
              <TH icon={rowLabelIcon} left>
                {rowLabel}
              </TH>
              <TH icon="chart">Min Trigger Value</TH>
              <TH icon="chart">Max Trigger Value</TH>
              <TH icon="chart">Actual Value</TH>
              <TH icon="scale">Difference</TH>
              <TH icon="chat">Remark</TH>
            </tr>
          </thead>
          <tbody>
            {locations.map((location, locationIndex) => (
              <React.Fragment
                key={scopeKey(location.id, location.subLocationId)}
              >
                {!singleLocation && (
                  <tr>
                    <td
                      colSpan="7"
                      style={{
                        padding: "16px 12px",
                        background:
                          "linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%)",
                        fontWeight: "700",
                        textAlign: "center",
                        color: "#2d5016",
                        fontSize: "16px",
                        borderTop:
                          locationIndex > 0 ? "3px solid #e0e7ff" : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "10px",
                        }}
                      >
                        <KpiIcon name="location" size={16} color="#2d5016" />
                        {location.name}
                      </div>
                    </td>
                  </tr>
                )}

                {rowsFor(location).map((row, rowIndex) => (
                  <AnalyticsRow
                    key={`${scopeKey(
                      location.id,
                      location.subLocationId
                    )}-${row.questionId}`}
                    index={rowIndex}
                    questionId={row.questionId}
                    name={row.name}
                    min={row.min}
                    max={row.max}
                    actual={row.actual}
                    difference={row.difference}
                    remark={row.remark}
                    stripeColor={stripeColor}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          padding: "0 8px",
        }}
      >
        <div>
          <h3
            style={{
              margin: "0 0 8px 0",
              color: "#1f2937",
              fontSize: "24px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <KpiIcon name={headerIcon} size={22} color="#1f2937" />
            {title}
          </h3>
          <p style={{ margin: "0", color: "#6b7280", fontSize: "14px" }}>
            {subtitle}
          </p>
        </div>
        <button
          onClick={exportToExcel}
          style={{
            padding: "12px 24px",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "#fff",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
          }}
        >
          <KpiIcon name="export" size={16} color="#fff" />
          Export to Excel
        </button>
      </div>
      {renderBody()}
    </div>
  );
};

export default AnalyticsTable;
